
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  CheckCircle2, AlertTriangle, Clock, DollarSign,
  RefreshCw, XCircle, FileText, Upload, X, Paperclip, Calendar, AlertCircle, ChevronDown, ChevronUp,
} from 'lucide-react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../../../src/contexts/AuthContext';
import { useToast } from '../../../../src/contexts/ToastContext';
import { api } from '../../../../src/services/api';
import { supabase } from '../../../../src/lib/supabase';
import { logUsageEvent } from '../../../../src/lib/logger';
import {
  formatCurrency, formatKwh,
  getTarifaVigente, isGrupoTarifarioA, calcularComissaoParceiro,
  calcularQuintoDiaUtil,
} from '../../../../src/lib/comissaoUtils';
import {
  calcularBonificacaoDasVendas, colunasDaBonificacao, donoDaVenda, liberacoesPixII,
  planejarGravacao, rotuloFaixaMes,
} from '../../../../src/lib/bonificacaoParceiroFolha';
import type { ComissaoParceiro, TarifaEnergia, Profile } from '../../../../types';

interface Props {
  comissoes: ComissaoParceiro[];
  parceiros: Profile[];
  tarifas: TarifaEnergia[];
  mes: number;
  ano: number;
  canEdit: boolean;
  todosUsuarios: Profile[];
  onRefresh: () => void;
}

// Days waiting helper
function diasSemReceber(comissao: ComissaoParceiro): { dias: number; urgencia: 'ok' | 'atencao' | 'critico' } | null {
  // If both parcels are paid, no waiting
  if (comissao.parcela_1_status === 'pago' && comissao.parcela_2_status === 'pago') return null;
  // If P1 is still pending and has a predicted date
  const baseDate = comissao.parcela_1_data_prevista
    ? new Date(comissao.parcela_1_data_prevista + 'T00:00:00')
    : (comissao as any).created_at
      ? new Date((comissao as any).created_at)
      : null;
  if (!baseDate) return null;
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const diff = Math.floor((now.getTime() - baseDate.getTime()) / (1000 * 60 * 60 * 24));
  if (diff <= 0) return { dias: 0, urgencia: 'ok' };
  if (diff <= 7) return { dias: diff, urgencia: 'ok' };
  if (diff <= 15) return { dias: diff, urgencia: 'atencao' };
  return { dias: diff, urgencia: 'critico' };
}

const PARCELA_CFG: Record<string, { label: string; cls: string; icon: React.ComponentType<any> }> = {
  pendente:   { label: 'Pendente',   cls: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300', icon: Clock },
  aprovado:   { label: 'Aprovado',   cls: 'bg-[#F6A937]/15 text-[#b85c00] dark:bg-[#F6A937]/10 dark:text-[#F6A937]', icon: CheckCircle2 },
  pago:       { label: 'Pago',       cls: 'bg-[#5F6C37]/10 text-[#3d4a22] dark:bg-[#5F6C37]/20 dark:text-[#84cc16]', icon: CheckCircle2 },
  cancelado:  { label: 'Cancelado',  cls: 'bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400', icon: XCircle },
  aguardando_primeira_fatura: { label: 'Ag. 1ª Fat.', cls: 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400', icon: Clock },
};

const ParceirosTab: React.FC<Props> = ({ comissoes, parceiros, tarifas, mes, ano, canEdit, todosUsuarios, onRefresh }) => {
  const { profile } = useAuth();
  const { addToast } = useToast();
  const [grupoAModal, setGrupoAModal] = useState<ComissaoParceiro | null>(null);
  const [grupoAObs, setGrupoAObs] = useState('');
  const [confirmModal, setConfirmModal] = useState<{ id: number; parcela: 1 | 2; comissao: ComissaoParceiro } | null>(null);
  const [paymentModal, setPaymentModal] = useState<{ id: number; parcela: 1 | 2; comissao: ComissaoParceiro } | null>(null);
  const [approvalModal, setApprovalModal] = useState<{ id: number; parcela: 1 | 2; comissao: ComissaoParceiro } | null>(null);
  const [resetModal, setResetModal] = useState<{ comissao: ComissaoParceiro } | null>(null);
  const [uploadingApproval, setUploadingApproval] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [uploading, setUploading] = useState(false);
  const [comprovanteFile, setComprovanteFile] = useState<File | null>(null);
  const [comprovantePreview, setComprovantePreview] = useState<string | null>(null);
  const [autoCalcRunning, setAutoCalcRunning] = useState(false);

  const isGerente = profile?.role === 'adm' || profile?.role === 'gerente';

  const comissoesRef = useRef(comissoes); comissoesRef.current = comissoes;
  const tarifasRef = useRef(tarifas); tarifasRef.current = tarifas;
  const autoCalcRunningRef = useRef(false);

  // Calcula e grava as bonificações das vendas de parceiro aceitas no mês.
  // - Aceite a partir de 26/10/2026 (Brasília): regra nova (bonificacaoParceiroFolha.ts),
  //   com a faixa somada por parceiro no mês — por isso os valores de quem ainda não
  //   recebeu nada são refeitos a cada abertura (a faixa sobe quando entra mais gente).
  // - Antes disso: regra antiga (calcularComissaoParceiro), conta inalterada.
  // Linha que já existe nunca tem status reescrito; linha com parcela paga não muda.
  const runAutoCalc = useCallback(async () => {
    if (autoCalcRunningRef.current) return;
    if (tarifasRef.current.length === 0) return;
    autoCalcRunningRef.current = true;
    setAutoCalcRunning(true);
    try {
      const mesStr = String(mes).padStart(2, '0');
      // Mês de Brasília (-03:00, sem horário de verão): o mesmo corte da faixa da regra nova
      const mesInicio = `${ano}-${mesStr}-01T00:00:00-03:00`;
      const lastDay = new Date(ano, mes, 0).getDate();
      const mesFim = `${ano}-${mesStr}-${lastDay}T23:59:59.999-03:00`;
      const negociacoes = await api.folhaPagamento.getNegociacoesAprovadas(mesInicio, mesFim);
      const tarifa = getTarifaVigente(tarifasRef.current, `${ano}-${mesStr}-01`);

      // Fetch existing vendedor commission negociacao_ids for this period
      // to avoid duplicating commissions for people who changed from vendedor to parceiro
      const { data: existingVendedorNegs } = await supabase
        .from('comissao_vendedor_negociacoes')
        .select('negociacao_id, comissao_vendedor:comissao_vendedores!inner(id)')
        .not('negociacao_id', 'is', null);
      const vendedorNegIds = new Set((existingVendedorNegs || []).map((r: any) => r.negociacao_id));

      // Vendas de parceiro do mês: quem recebe e qual regra
      const vendas = (negociacoes as any[])
        .map(neg => ({ neg, parceiroId: donoDaVenda(neg.responsavel) }))
        // Skip if this negotiation already has a vendedor commission
        // (person was vendedor when this sale was made, later changed to parceiro)
        .filter(v => v.parceiroId && !vendedorNegIds.has(v.neg.id)) as { neg: any; parceiroId: string }[];

      const bonificacoes = calcularBonificacaoDasVendas(vendas.map(({ neg, parceiroId }) => ({
        negociacao_id: neg.id,
        dono_id: parceiroId,
        decisao_final_at: neg.analise_cadastral?.[0]?.decisao_final_at,
        consumo_medio_kwh: neg.consumo_medio_kwh,
        cpf_cnpj: neg.cpf_cnpj,
        ucs: neg.unidades_consumidoras,
      })));

      const existentes = await api.folhaPagamento.getComissoesParceirosDasNegociacoes(vendas.map(v => v.neg.id));
      const existentePor = new Map(existentes.map(e => [`${e.parceiro_id}|${e.negociacao_id}`, e]));

      let gravou = false;
      for (const { neg, parceiroId } of vendas) {
        const bonificacao = bonificacoes.get(neg.id);
        let valores: Record<string, unknown>;
        let iniciais: Record<string, unknown>;
        if (bonificacao) {
          valores = colunasDaBonificacao(bonificacao);
          iniciais = { parcela_1_status: 'pendente', parcela_2_status: 'aguardando_primeira_fatura' };
        } else {
          if (!tarifa) continue; // regra antiga precisa da tarifa vigente
          const consumo = Number(neg.consumo_medio_kwh) || 0;
          const uc = neg.unidades_consumidoras?.[0];
          const grupoA = isGrupoTarifarioA(uc?.grupo_tarifario);
          const calc = calcularComissaoParceiro(consumo, tarifa.valor);
          const ac = neg.analise_cadastral?.[0];
          // P1 predicted date: 5 calendar days after analysis approval date
          const approvalDate = ac?.updated_at ? new Date(ac.updated_at) : null;
          let p1DataPrevista: string | undefined;
          if (approvalDate) {
            const dtBase = new Date(approvalDate.getTime() + 5 * 24 * 60 * 60 * 1000);
            p1DataPrevista = dtBase.toISOString().split('T')[0];
          }
          valores = {
            consumo_medio_kwh: consumo, tarifa_energia_valor: tarifa.valor, tarifa_energia_id: tarifa.id,
            faixa_comissao_percentual: grupoA ? 0 : calc.faixaPercentual,
            valor_base_comissao: grupoA ? 0 : calc.valorBase,
            parcela_1_valor: grupoA ? 0 : calc.parcela1,
            parcela_1_data_prevista: p1DataPrevista,
            parcela_2_valor: grupoA ? 0 : calc.parcela2,
            is_grupo_a: grupoA,
          };
          iniciais = {
            parcela_1_status: 'pendente',
            parcela_2_status: 'aguardando_primeira_fatura',
            ...(grupoA ? { grupo_a_status: 'pendente_aprovacao_gerente' } : {}),
          };
        }

        // Uma venda que falha ao gravar não segura as outras do mês
        const plano = planejarGravacao(existentePor.get(`${parceiroId}|${neg.id}`), valores, iniciais);
        try {
          if (plano.acao === 'inserir') {
            await api.folhaPagamento.inserirComissaoParceiro({ parceiro_id: parceiroId, negociacao_id: neg.id, ...plano.dados });
            gravou = true;
          } else if (plano.acao === 'atualizar') {
            await api.folhaPagamento.updateComissaoParceiro(plano.id, plano.dados as Partial<ComissaoParceiro>);
            gravou = true;
          }
        } catch (err) { console.error(`Auto-calc parceiros: negociação ${neg.id}`, err); }
      }

      // Pix II da regra nova (de qualquer mês): sai de "aguardando 1ª fatura" quando o
      // consumidor paga a primeira, com vencimento no dia 10 do mês seguinte ao pagamento.
      const aguardando = await api.folhaPagamento.getComissoesParceirosAguardandoFatura();
      if (aguardando.length > 0) {
        const pagos = await api.folhaPagamento.getPrimeiroPagamentoDasNegociacoes(aguardando.map(a => a.negociacao_id));
        for (const lib of liberacoesPixII(aguardando, pagos)) {
          await api.folhaPagamento.updateComissaoParceiro(lib.id, {
            parcela_2_status: lib.parcela_2_status,
            parcela_2_data_prevista: lib.parcela_2_data_prevista,
          });
          gravou = true;
        }
      }

      if (gravou) onRefresh();
    } catch (err) { console.error('Auto-calc parceiros error:', err); }
    finally { autoCalcRunningRef.current = false; setAutoCalcRunning(false); }
  }, [mes, ano]);

  useEffect(() => { runAutoCalc(); }, [mes, ano]);

  const configKey = useMemo(() => tarifas.map(t => `${t.id}:${t.valor}`).join(','), [tarifas]);
  const prevConfigKey = useRef('');
  useEffect(() => {
    if (prevConfigKey.current && prevConfigKey.current !== configKey) runAutoCalc();
    prevConfigKey.current = configKey;
  }, [configKey, runAutoCalc]);

  const handleParcelaStatus = async (id: number, parcela: 1 | 2, status: 'aprovado' | 'pago' | 'cancelado') => {
    if (status === 'pago') {
      const comissao = comissoes.find(c => c.id === id);
      if (comissao) { setConfirmModal({ id, parcela, comissao }); }
      return;
    }
    if (status === 'aprovado') {
      const comissao = comissoes.find(c => c.id === id);
      if (comissao) { setApprovalModal({ id, parcela, comissao }); }
      return;
    }
    try {
      const updates = parcela === 1 ? { parcela_1_status: status } : { parcela_2_status: status };
      await api.folhaPagamento.updateComissaoParceiro(id, updates as any);
      addToast(`Parcela ${parcela} marcada como ${status}`, 'success');
      logUsageEvent({ userId: profile?.id, modulo: 'financeiro', funcionalidade: 'folha_pagamento', acao: 'mudar_status_parcela_comissao', origem: 'acao' });
      onRefresh();
    } catch { addToast('Erro ao atualizar', 'error'); }
  };

  const handleConfirmApproval = async () => {
    if (!approvalModal) return;
    setUploadingApproval(true);
    try {
      const updates = approvalModal.parcela === 1 
        ? { parcela_1_status: 'aprovado', parcela_1_aprovado_por: profile?.id, parcela_1_aprovado_em: new Date().toISOString() } 
        : { parcela_2_status: 'aprovado', parcela_2_aprovado_por: profile?.id, parcela_2_aprovado_em: new Date().toISOString() };
      await api.folhaPagamento.updateComissaoParceiro(approvalModal.id, updates as any);
      addToast(`Parcela ${approvalModal.parcela} aprovada!`, 'success');
      logUsageEvent({ userId: profile?.id, modulo: 'financeiro', funcionalidade: 'folha_pagamento', acao: 'aprovar_comissao', origem: 'acao' });
      setApprovalModal(null);
      onRefresh();
    } catch { addToast('Erro ao aprovar', 'error'); }
    finally { setUploadingApproval(false); }
  };

  const handleConfirmReset = async () => {
    if (!resetModal) return;
    setResetting(true);
    try {
      await api.folhaPagamento.updateComissaoParceiro(resetModal.comissao.id, {
        parcela_1_status: 'pendente', parcela_1_aprovado_por: null, parcela_1_aprovado_em: null, parcela_1_pago_por: null, parcela_1_data_pagamento: null, comprovante_url_p1: null,
        parcela_2_status: 'pendente', parcela_2_aprovado_por: null, parcela_2_aprovado_em: null, parcela_2_pago_por: null, parcela_2_data_pagamento: null, comprovante_url_p2: null,
      } as any);
      addToast('Aprovações e pagamentos resetados.', 'success');
      logUsageEvent({ userId: profile?.id, modulo: 'financeiro', funcionalidade: 'folha_pagamento', acao: 'resetar_status_comissao', origem: 'acao' });
      setResetModal(null);
      onRefresh();
    } catch { addToast('Erro ao resetar', 'error'); }
    finally { setResetting(false); }
  };

  const handleConfirmProceed = () => {
    if (!confirmModal) return;
    setPaymentModal(confirmModal);
    setConfirmModal(null);
  };

  const closePaymentModal = () => {
    setPaymentModal(null); setComprovanteFile(null); setComprovantePreview(null);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { addToast('Arquivo muito grande (máx 5MB)', 'error'); return; }
    setComprovanteFile(file);
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = () => setComprovantePreview(reader.result as string);
      reader.readAsDataURL(file);
    } else { setComprovantePreview(null); }
  };

  const handleConfirmPayment = async () => {
    if (!paymentModal || !comprovanteFile) { addToast('O comprovante PIX é obrigatório', 'error'); return; }
    setUploading(true);
    try {
      const fileExt = comprovanteFile.name.split('.').pop();
      const fileName = `parceiros/${paymentModal.id}-p${paymentModal.parcela}-${Date.now()}.${fileExt}`;
      const { error: uploadError } = await supabase.storage.from('comprovantes').upload(fileName, comprovanteFile, { upsert: true });
      if (uploadError) throw uploadError;
      const { data: urlData } = supabase.storage.from('comprovantes').getPublicUrl(fileName);
      const comprovanteUrl = urlData.publicUrl;
      const updates = paymentModal.parcela === 1
        ? { parcela_1_status: 'pago', parcela_1_data_pagamento: new Date().toISOString().split('T')[0], comprovante_url_p1: comprovanteUrl, comprovante_nome_p1: comprovanteFile.name, parcela_1_pago_por: profile?.id }
        : { parcela_2_status: 'pago', parcela_2_data_pagamento: new Date().toISOString().split('T')[0], comprovante_url_p2: comprovanteUrl, comprovante_nome_p2: comprovanteFile.name, parcela_2_pago_por: profile?.id };
      await api.folhaPagamento.updateComissaoParceiro(paymentModal.id, updates as any);
      addToast(`Parcela ${paymentModal.parcela} paga com sucesso!`, 'success');
      logUsageEvent({ userId: profile?.id, modulo: 'financeiro', funcionalidade: 'folha_pagamento', acao: 'confirmar_pagamento_comissao', origem: 'acao' });
      closePaymentModal();
      onRefresh();
    } catch (err) { console.error(err); addToast('Erro ao confirmar pagamento', 'error'); }
    finally { setUploading(false); }
  };

  const handleGrupoAApproval = async (approved: boolean) => {
    if (!grupoAModal) return;
    try {
      await api.folhaPagamento.updateComissaoParceiro(grupoAModal.id, {
        grupo_a_status: approved ? 'aprovado_manual' : 'rejeitado',
        grupo_a_aprovado_por: profile?.id,
        grupo_a_aprovado_em: new Date().toISOString(),
        grupo_a_observacoes: grupoAObs,
      } as any);
      addToast(approved ? 'Aprovado (cálculo manual)' : 'Rejeitado', 'success');
      logUsageEvent({ userId: profile?.id, modulo: 'financeiro', funcionalidade: 'folha_pagamento', acao: 'decidir_grupo_a_comissao', origem: 'acao' });
      setGrupoAModal(null); setGrupoAObs('');
      onRefresh();
    } catch { addToast('Erro', 'error'); }
  };

  // Regra nova: Grupo A não pode assinar — fica na tabela, zerado e com o motivo,
  // em vez de ir para a avaliação manual do gerente (que é da regra antiga).
  const normais = comissoes.filter(c => (!c.is_grupo_a || !!c.regra) && c.negociacao?.analise_cadastral?.[0] && ['Aprovado', 'Aprovado com Ressalvas'].includes(c.negociacao.analise_cadastral[0].decisao_final));
  const grupoA = comissoes.filter(c => c.is_grupo_a && !c.regra);

  const formatDate = (dateStr?: string | null) => {
    if (!dateStr) return '—';
    const [y, m, d] = dateStr.split('-');
    return `${d}/${m}/${y}`;
  };

  const totalComissoes = normais.reduce((s, c) => s + c.valor_base_comissao, 0);
  const totalP1Pago = normais.filter(c => c.parcela_1_status === 'pago').reduce((s, c) => s + c.parcela_1_valor, 0);
  const totalP2Pago = normais.filter(c => c.parcela_2_status === 'pago').reduce((s, c) => s + c.parcela_2_valor, 0);

  const renderBadge = (status: string) => {
    const cfg = PARCELA_CFG[status] || PARCELA_CFG.pendente;
    const Icon = cfg.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${cfg.cls}`}>
        <Icon size={9} />{cfg.label}
      </span>
    );
  };

  return (
    <div className="space-y-5">

      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h3 className="font-bold text-lg text-gray-800 dark:text-white">Bonificação de Parceiros</h3>
          {normais.length > 0 && (
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{normais.length} negociação(ões) · {formatCurrency(totalComissoes)} total</p>
          )}
        </div>
        {autoCalcRunning && (
          <div className="flex items-center gap-2 bg-[#F6A937]/10 px-3 py-1.5 rounded-lg">
            <RefreshCw size={13} className="animate-spin text-[#F6A937]" />
            <span className="text-[#b85c00] dark:text-[#F6A937] font-medium text-xs">Calculando...</span>
          </div>
        )}
      </div>

      {/* Summary strip */}
      {normais.length > 0 && (
        <div className="grid grid-cols-3 gap-3" style={{ animation: 'fadeSlideUp 0.4s ease-out' }}>
          {[
            { label: 'Total Bonificação', value: formatCurrency(totalComissoes), color: 'text-gray-900 dark:text-white' },
            { label: 'P1 já pago', value: formatCurrency(totalP1Pago), color: 'text-[#5F6C37] dark:text-[#84cc16]' },
            { label: 'P2 já pago', value: formatCurrency(totalP2Pago), color: 'text-[#5F6C37] dark:text-[#84cc16]' },
          ].map((s, i) => (
            <div key={i} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 px-4 py-3 shadow-sm">
              <p className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide">{s.label}</p>
              <p className={`text-base font-black tabular-nums mt-0.5 ${s.color}`}>{s.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Grupo A Alert */}
      {grupoA.length > 0 && (
        <div className="bg-[#F6A937]/8 dark:bg-[#F6A937]/5 border border-[#F6A937]/40 dark:border-[#F6A937]/20 rounded-xl p-4" style={{ animation: 'fadeSlideUp 0.45s ease-out' }}>
          <h4 className="font-bold text-[#b85c00] dark:text-[#F6A937] flex items-center gap-2 text-sm mb-3">
            <AlertTriangle size={15} /> Grupo Tarifário A — Cálculo Manual ({grupoA.length})
          </h4>
          <div className="space-y-2">
            {grupoA.map(c => (
              <div key={c.id} className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg border border-[#F6A937]/20 dark:border-[#F6A937]/10">
                <div>
                  <span className="text-sm font-semibold text-gray-900 dark:text-white">{c.parceiro?.full_name}</span>
                  <span className="text-xs text-gray-400 dark:text-gray-500 ml-2">Neg #{c.negociacao_id} · {formatKwh(c.consumo_medio_kwh)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                    c.grupo_a_status === 'aprovado_manual' ? 'bg-[#5F6C37]/10 text-[#5F6C37] dark:text-[#84cc16]' :
                    c.grupo_a_status === 'rejeitado' ? 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400' :
                    'bg-[#F6A937]/15 text-[#b85c00] dark:text-[#F6A937]'
                  }`}>
                    {c.grupo_a_status === 'aprovado_manual' ? 'Aprovado Manual' : c.grupo_a_status === 'rejeitado' ? 'Rejeitado' : 'Pendente'}
                  </span>
                  {isGerente && c.grupo_a_status === 'pendente_aprovacao_gerente' && (
                    <button onClick={() => { setGrupoAModal(c); setGrupoAObs(''); }}
                      className="px-3 py-1 text-xs font-bold bg-[#F6A937] hover:bg-[#e09620] text-white rounded-lg transition-colors">
                      Avaliar
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty state */}
      {normais.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-14 text-center">
          <FileText size={40} className="mx-auto text-gray-200 dark:text-gray-600 mb-3" />
          <p className="text-gray-500 dark:text-gray-400 font-semibold text-sm">Nenhuma bonificação de parceiro calculada</p>
          <p className="text-gray-400 dark:text-gray-500 text-xs mt-1">As bonificações são geradas automaticamente a partir de negociações com cadastro aprovado.</p>
        </div>
      ) : (
        /* ── TABLE ── */
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden" style={{ animation: 'fadeSlideUp 0.5s ease-out' }}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-800/60">
                  <th className="text-left px-4 py-3 font-semibold text-gray-500 dark:text-gray-400 text-[11px] uppercase tracking-wide">Parceiro</th>
                  <th className="text-right px-3 py-3 font-semibold text-gray-500 dark:text-gray-400 text-[11px] uppercase tracking-wide">Consumo</th>
                  <th className="text-center px-3 py-3 font-semibold text-gray-500 dark:text-gray-400 text-[11px] uppercase tracking-wide">Faixa</th>
                  <th className="text-right px-3 py-3 font-semibold text-gray-500 dark:text-gray-400 text-[11px] uppercase tracking-wide">Total</th>
                  <th className="text-center px-3 py-3 font-semibold text-gray-500 dark:text-gray-400 text-[11px] uppercase tracking-wide">P1 (30%)</th>
                  <th className="text-center px-3 py-3 font-semibold text-gray-500 dark:text-gray-400 text-[11px] uppercase tracking-wide">P2 (70%)</th>
                  <th className="text-center px-3 py-3 font-semibold text-gray-500 dark:text-gray-400 text-[11px] uppercase tracking-wide">
                    <span className="flex items-center justify-center gap-1">
                      <AlertCircle size={11} />
                      Espera
                    </span>
                  </th>
                  {canEdit && <th className="px-3 py-3 w-32 text-right">Ações</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50 dark:divide-gray-700/40">
                {normais.map((c, idx) => {
                  const initials = (c.parceiro?.full_name || 'N').split(' ').filter(Boolean).map((n: string) => n[0]).join('').slice(0, 2).toUpperCase();
                  const wait = diasSemReceber(c);
                  const p1PagoPor = todosUsuarios.find(u => u.id === (c as any).parcela_1_pago_por);
                  const p2PagoPor = todosUsuarios.find(u => u.id === (c as any).parcela_2_pago_por);
                  const expandRow = expandedId === c.id;
                  const ac = c.negociacao?.analise_cadastral?.[0];
                  const novaRegra = !!c.regra;
                  const pctDe = (v: number | null | undefined) => `${Math.round((Number(v) || 0) * 100)}%`;

                  return (
                    <React.Fragment key={c.id}>
                    <tr 
                      onClick={() => setExpandedId(expandRow ? null : c.id)}
                      className="hover:bg-gray-50/70 dark:hover:bg-gray-700/20 transition-colors cursor-pointer"
                      style={{ animation: `fadeSlideUp ${0.35 + idx * 0.04}s ease-out` }}
                    >
                      {/* Parceiro */}
                      <td className="px-4 py-3.5">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-xl bg-[rgba(95,108,55,0.9)] flex items-center justify-center text-white text-[10px] font-black flex-shrink-0 shadow-sm">
                            {initials}
                          </div>
                          <div className="min-w-0">
                            <p className="font-semibold text-sm text-gray-900 dark:text-white truncate">{c.parceiro?.full_name || 'N/A'}</p>
                            <p className="text-[11px] text-gray-400 dark:text-gray-500 truncate">Neg #{c.negociacao_id}{c.negociacao?.cliente_nome ? ` · ${c.negociacao.cliente_nome}` : ''}</p>
                          </div>
                        </div>
                      </td>

                      {/* Consumo */}
                      <td className="px-3 py-3.5 text-right text-gray-600 dark:text-gray-300 tabular-nums text-[13px]">
                        {formatKwh(c.consumo_medio_kwh)}
                        {novaRegra && c.desconto_percentual != null && (
                          <span className="block text-[10px] text-gray-400 dark:text-gray-500 whitespace-nowrap">
                            desc. {c.desconto_percentual}%{c.redutor != null ? ` · redutor ${pctDe(c.redutor)}` : ''}
                          </span>
                        )}
                      </td>

                      {/* Faixa */}
                      <td className="px-3 py-3.5 text-center">
                        {novaRegra ? (
                          <div className="flex flex-col items-center gap-0.5" title={rotuloFaixaMes(c.faixa_mes, c.faixa_comissao_percentual, c.kwh_mes)}>
                            <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-black bg-[#5F6C37]/10 dark:bg-[#5F6C37]/20 text-[#5F6C37] dark:text-[#84cc16] whitespace-nowrap">
                              {c.faixa_mes ? `${c.faixa_mes} · ${c.faixa_comissao_percentual}%` : 'Sem faixa'}
                            </span>
                            <span className="text-[9px] text-gray-400 dark:text-gray-500 tabular-nums whitespace-nowrap">
                              {formatKwh(Number(c.kwh_mes) || 0)} no mês
                            </span>
                          </div>
                        ) : (
                          <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-black bg-[#5F6C37]/10 dark:bg-[#5F6C37]/20 text-[#5F6C37] dark:text-[#84cc16]">
                            {c.faixa_comissao_percentual}%
                          </span>
                        )}
                      </td>

                      {/* Total */}
                      <td className="px-3 py-3.5 text-right font-black text-gray-900 dark:text-white tabular-nums">
                        {formatCurrency(c.valor_base_comissao)}
                        {novaRegra && c.motivo_fora && (
                          <span className="flex items-center justify-end gap-1 text-[10px] font-semibold text-[#b85c00] dark:text-[#F6A937] mt-0.5" title="Este consumidor não entra na conta do mês">
                            <AlertTriangle size={10} className="shrink-0" />
                            <span className="truncate max-w-[180px]">Fora da conta: {c.motivo_fora}</span>
                          </span>
                        )}
                      </td>

                      {/* P1 */}
                      <td className="px-3 py-3.5">
                        <div className="flex flex-col items-center gap-1">
                          {novaRegra && <span className="text-[9px] font-bold uppercase tracking-wide text-gray-400 dark:text-gray-500">Pix I</span>}
                          <span className="text-xs font-bold text-gray-700 dark:text-gray-200 tabular-nums">{formatCurrency(c.parcela_1_valor)}</span>
                          {renderBadge(c.parcela_1_status)}
                          {c.parcela_1_data_prevista && c.parcela_1_status !== 'pago' && (
                            <span className="text-[9px] text-gray-400 dark:text-gray-500 flex items-center gap-0.5">
                              <Calendar size={8} />{formatDate(c.parcela_1_data_prevista as string)}
                            </span>
                          )}
                          {c.parcela_1_status === 'pago' && (c as any).comprovante_url_p1 && (
                            <a href={(c as any).comprovante_url_p1} target="_blank" rel="noopener noreferrer"
                              className="text-[9px] text-[#5F6C37] dark:text-[#84cc16] hover:underline flex items-center gap-0.5">
                              <Paperclip size={8} />Comprov.
                            </a>
                          )}
                          {c.parcela_1_status !== 'pendente' && (c as any).parcela_1_aprovado_por && (
                            <span className="text-[9px] text-gray-400 dark:text-gray-500" title="Aprovado por">
                              ✓ {todosUsuarios.find(u => u.id === (c as any).parcela_1_aprovado_por)?.full_name?.split(' ')[0]}
                            </span>
                          )}
                          {p1PagoPor && c.parcela_1_status === 'pago' && (
                            <span className="text-[9px] text-gray-400 dark:text-gray-500" title="Pago por">
                              $ {p1PagoPor.full_name?.split(' ')[0]}
                            </span>
                          )}
                        </div>
                      </td>

                      {/* P2 */}
                      <td className="px-3 py-3.5">
                        <div className="flex flex-col items-center gap-1">
                          {novaRegra && <span className="text-[9px] font-bold uppercase tracking-wide text-gray-400 dark:text-gray-500">Pix II</span>}
                          <span className="text-xs font-bold text-gray-700 dark:text-gray-200 tabular-nums">{formatCurrency(c.parcela_2_valor)}</span>
                          {renderBadge(c.parcela_2_status)}
                          {c.parcela_2_data_prevista && c.parcela_2_status !== 'pago' && c.parcela_2_status !== 'aguardando_primeira_fatura' && (
                            <span className="text-[9px] text-gray-400 dark:text-gray-500 flex items-center gap-0.5">
                              <Calendar size={8} />{formatDate(c.parcela_2_data_prevista as string)}
                            </span>
                          )}
                          {c.parcela_2_status === 'pago' && (c as any).comprovante_url_p2 && (
                            <a href={(c as any).comprovante_url_p2} target="_blank" rel="noopener noreferrer"
                              className="text-[9px] text-[#5F6C37] dark:text-[#84cc16] hover:underline flex items-center gap-0.5">
                              <Paperclip size={8} />Comprov.
                            </a>
                          )}
                          {c.parcela_2_status !== 'pendente' && c.parcela_2_status !== 'aguardando_primeira_fatura' && (c as any).parcela_2_aprovado_por && (
                            <span className="text-[9px] text-gray-400 dark:text-gray-500" title="Aprovado por">
                              ✓ {todosUsuarios.find(u => u.id === (c as any).parcela_2_aprovado_por)?.full_name?.split(' ')[0]}
                            </span>
                          )}
                          {p2PagoPor && c.parcela_2_status === 'pago' && (
                            <span className="text-[9px] text-gray-400 dark:text-gray-500" title="Pago por">
                              $ {p2PagoPor.full_name?.split(' ')[0]}
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Dias de Espera */}
                      <td className="px-3 py-3.5 text-center">
                        {wait === null ? (
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded-full bg-[#5F6C37]/10 text-[#5F6C37] dark:text-[#84cc16]">
                            <CheckCircle2 size={10} /> Quitado
                          </span>
                        ) : wait.dias === 0 ? (
                          <span className="text-[11px] text-gray-400 dark:text-gray-500">No prazo</span>
                        ) : (
                          <div className={`inline-flex flex-col items-center gap-0.5 px-2.5 py-1.5 rounded-lg border ${
                            wait.urgencia === 'critico'
                              ? 'bg-red-50 dark:bg-red-900/15 border-red-200 dark:border-red-800/40'
                              : wait.urgencia === 'atencao'
                                ? 'bg-[#F6A937]/10 border-[#F6A937]/30 dark:border-[#F6A937]/20'
                                : 'bg-gray-50 dark:bg-gray-700/40 border-gray-200 dark:border-gray-600'
                          }`}>
                            <span className={`text-lg font-black leading-none tabular-nums ${
                              wait.urgencia === 'critico'
                                ? 'text-red-600 dark:text-red-400'
                                : wait.urgencia === 'atencao'
                                  ? 'text-[#b85c00] dark:text-[#F6A937]'
                                  : 'text-gray-600 dark:text-gray-300'
                            }`}>
                              {wait.dias}
                            </span>
                            <span className={`text-[8px] font-bold uppercase tracking-wider ${
                              wait.urgencia === 'critico'
                                ? 'text-red-500 dark:text-red-400'
                                : wait.urgencia === 'atencao'
                                  ? 'text-[#b85c00] dark:text-[#F6A937]'
                                  : 'text-gray-400 dark:text-gray-500'
                            }`}>
                              {wait.dias === 1 ? 'DIA' : 'DIAS'}
                            </span>
                          </div>
                        )}
                      </td>

                      {/* Actions */}
                      <td className="px-3 py-3.5" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-1 justify-end">
                          {canEdit && c.parcela_1_status === 'pendente' && (
                            <button onClick={() => handleParcelaStatus(c.id, 1, 'aprovado')} className="p-1.5 text-[#5F6C37] hover:bg-[#5F6C37]/10 dark:hover:bg-[#5F6C37]/20 rounded-lg transition-colors bg-gray-50 dark:bg-gray-700/50" title="Aprovar P1">
                              <CheckCircle2 size={14} /> P1
                            </button>
                          )}
                          {canEdit && (c.parcela_1_status === 'pendente' || c.parcela_1_status === 'aprovado') && (
                            <button onClick={() => handleParcelaStatus(c.id, 1, 'pago')} className="p-1.5 text-[#5F6C37] hover:bg-[#5F6C37]/10 dark:hover:bg-[#5F6C37]/20 rounded-lg transition-colors bg-gray-50 dark:bg-gray-700/50" title="Pagar P1">
                              <DollarSign size={14} /> P1
                            </button>
                          )}
                          {canEdit && c.parcela_2_status === 'pendente' && (
                            <button onClick={() => handleParcelaStatus(c.id, 2, 'aprovado')} className="p-1.5 text-[#5F6C37] hover:bg-[#5F6C37]/10 dark:hover:bg-[#5F6C37]/20 rounded-lg transition-colors bg-gray-50 dark:bg-gray-700/50" title="Aprovar P2">
                              <CheckCircle2 size={14} /> P2
                            </button>
                          )}
                          {canEdit && (c.parcela_2_status === 'pendente' || c.parcela_2_status === 'aprovado') && (
                            <button onClick={() => handleParcelaStatus(c.id, 2, 'pago')} className="p-1.5 text-[#5F6C37] hover:bg-[#5F6C37]/10 dark:hover:bg-[#5F6C37]/20 rounded-lg transition-colors bg-gray-50 dark:bg-gray-700/50" title="Pagar P2">
                              <DollarSign size={14} /> P2
                            </button>
                          )}
                          {profile?.role === 'adm' && (c.parcela_1_status !== 'pendente' || c.parcela_2_status !== 'pendente' && c.parcela_2_status !== 'aguardando_primeira_fatura') && (
                             <button onClick={(e) => { e.stopPropagation(); setResetModal({ comissao: c }); }} className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors" title="Resetar">
                               <RefreshCw size={14} />
                             </button>
                          )}
                          <div className="ml-1 text-gray-400">
                             {expandRow ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                          </div>
                        </div>
                      </td>
                    </tr>
                    {expandRow && (
                      <tr className="bg-gray-50/50 dark:bg-gray-800/50">
                        <td colSpan={8} className="p-4 border-t border-gray-100 dark:border-gray-700/40">
                            <div className="flex items-center gap-4 flex-wrap text-sm">
                               {ac ? (
                                 <div className="flex bg-white dark:bg-gray-900 p-2.5 rounded-xl border border-gray-200 dark:border-gray-700 gap-4 flex-wrap">
                                   <div className="text-xs text-gray-600 dark:text-gray-300">
                                     <span className="font-semibold text-gray-900 dark:text-white block">Status da Análise</span>
                                     <div className="flex items-center gap-1 mt-1 text-[#5F6C37] dark:text-[#84cc16]">
                                       <CheckCircle2 size={14} />
                                       {ac.decisao_final || 'Aprovada'} {ac.updated_at ? `em ${new Date(ac.updated_at).toLocaleDateString('pt-BR')}` : ''}
                                     </div>
                                   </div>
                                   <div className="text-xs text-gray-600 dark:text-gray-300 border-l border-gray-200 dark:border-gray-700 pl-4">
                                     <span className="font-semibold text-gray-900 dark:text-white block">Analista</span>
                                     <span className="mt-1 block">{todosUsuarios.find(u => u.id === ac.analista_id)?.full_name || 'Desconhecido'}</span>
                                   </div>
                                   <div className="flex items-center gap-2 border-l border-gray-200 dark:border-gray-700 pl-4">
                                      <a href={`/#/gestao?negociacaoId=${c.negociacao_id}`} onClick={e => e.stopPropagation()} className="px-3 py-1.5 bg-[#5F6C37]/10 text-[#5F6C37] dark:bg-[#5F6C37]/20 dark:text-[#84cc16] font-semibold text-xs rounded-lg hover:bg-[#5F6C37]/20 transition-colors">
                                        Ver Análise
                                      </a>
                                      <a href={`/#/crm?negociacaoId=${c.negociacao_id}`} onClick={e => e.stopPropagation()} className="px-3 py-1.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-semibold text-xs rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors">
                                        Ir para CRM
                                      </a>
                                   </div>
                                 </div>
                               ) : (
                                 <p className="text-xs text-gray-400">Validação de Dados não encontrada para esta negociação.</p>
                               )}
                               {novaRegra && (
                                 <div className="bg-white dark:bg-gray-900 p-2.5 rounded-xl border border-gray-200 dark:border-gray-700 text-xs text-gray-600 dark:text-gray-300 space-y-1">
                                   <span className="font-semibold text-gray-900 dark:text-white block">Bonificação — regra de 26/10/2026</span>
                                   {c.motivo_fora ? (
                                     <p className="text-[#b85c00] dark:text-[#F6A937]">Não entra na conta do mês: {c.motivo_fora}.</p>
                                   ) : (
                                     <p className="tabular-nums">
                                       Fatura com desconto {formatCurrency(Number(c.fatura_com_desconto) || 0)}
                                       {' × '}{c.faixa_mes} ({c.faixa_comissao_percentual}%)
                                       {' × '}redutor {pctDe(c.redutor)}
                                       {' = '}<strong className="text-gray-900 dark:text-white">{formatCurrency(c.valor_base_comissao)}</strong>
                                     </p>
                                   )}
                                   <p className="text-gray-400 dark:text-gray-500">
                                     {rotuloFaixaMes(c.faixa_mes, c.faixa_comissao_percentual, c.kwh_mes)} (vendas aceitas do parceiro no mês).
                                     {' '}Pix I no dia 10 do mês seguinte ao aceite; Pix II no dia 10 do mês seguinte à 1ª fatura paga.
                                   </p>
                                 </div>
                               )}
                            </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ══════ MODALS ══════ */}

      {/* Grupo A Modal */}
      {grupoAModal && createPortal(
        <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4" style={{ animation: 'modalBackdropIn 0.25s ease-out' }}>
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setGrupoAModal(null)} />
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-6 w-full max-w-md relative z-10 border border-gray-100 dark:border-gray-700" style={{ animation: 'modalSlideIn 0.3s cubic-bezier(0.16,1,0.3,1)' }} onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-[#F6A937]/15 flex items-center justify-center"><AlertTriangle size={16} className="text-[#F6A937]" /></div>
              Avaliação Grupo Tarifário A
            </h3>
            <div className="space-y-2 mb-4 text-sm">
              <p><span className="text-gray-400">Parceiro:</span> <strong className="text-gray-900 dark:text-white">{grupoAModal.parceiro?.full_name}</strong></p>
              <p><span className="text-gray-400">Negociação:</span> <strong className="text-gray-900 dark:text-white">#{grupoAModal.negociacao_id}</strong></p>
              <p><span className="text-gray-400">Consumo:</span> <strong className="text-gray-900 dark:text-white">{formatKwh(grupoAModal.consumo_medio_kwh)}</strong></p>
            </div>
            <textarea value={grupoAObs} onChange={e => setGrupoAObs(e.target.value)} placeholder="Observações (obrigatório)..."
              className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-900 text-sm text-gray-900 dark:text-white resize-none h-20 mb-4 focus:outline-none focus:ring-2 focus:ring-[#5F6C37]/30" />
            <div className="flex gap-3">
              <button onClick={() => handleGrupoAApproval(false)} disabled={!grupoAObs.trim()} className="flex-1 px-4 py-2.5 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 rounded-xl text-sm font-semibold hover:bg-red-50 dark:hover:bg-red-900/10 disabled:opacity-40 transition-colors">Rejeitar</button>
              <button onClick={() => handleGrupoAApproval(true)} disabled={!grupoAObs.trim()} className="flex-1 px-4 py-2.5 bg-[rgba(95,108,55,0.9)] text-white rounded-xl text-sm font-semibold disabled:opacity-40 transition-all">Aprovar (Manual)</button>
            </div>
          </div>
        </div>, document.body,
      )}

      {/* Approval Modal */}
      {approvalModal && createPortal(
        <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4" style={{ animation: 'modalBackdropIn 0.25s ease-out' }}>
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setApprovalModal(null)} />
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-6 w-full max-w-sm relative z-10 border border-gray-100 dark:border-gray-700 text-center" style={{ animation: 'modalSlideIn 0.3s' }}>
            <div className="w-14 h-14 rounded-full bg-[rgba(95,108,55,0.1)] flex items-center justify-center mx-auto mb-4 text-[#5F6C37]">
               <CheckCircle2 size={30} />
            </div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Aprovar Parcela {approvalModal.parcela}?</h3>
            <p className="text-sm text-gray-500 mb-6">Confirma a aprovação desta parcela para <strong>{approvalModal.comissao.parceiro?.full_name}</strong>?</p>
            <div className="flex gap-3">
              <button onClick={() => setApprovalModal(null)} className="flex-1 px-4 py-3 border rounded-xl text-gray-500 font-semibold">Cancelar</button>
              <button onClick={handleConfirmApproval} disabled={uploadingApproval} className="flex-1 px-4 py-3 bg-[#5F6C37] text-white rounded-xl font-bold flex items-center justify-center gap-2">
                 {uploadingApproval ? 'Aprovando...' : 'Aprovar'}
              </button>
            </div>
          </div>
        </div>, document.body
      )}

      {/* Reset Modal */}
      {resetModal && createPortal(
        <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4">
           <div className="absolute inset-0 bg-black/60" onClick={() => setResetModal(null)} />
           <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-6 w-full max-w-sm relative z-10 text-center">
             <div className="w-12 h-12 rounded-full bg-red-100 text-red-500 flex items-center justify-center mx-auto mb-4"><RefreshCw size={24} /></div>
             <h3 className="text-lg font-bold">Resetar Pagamento e Aprovação?</h3>
             <p className="text-sm text-gray-500 mb-6">Esta comissão voltará para o status "pendente" nas 2 parcelas.</p>
             <div className="flex gap-3">
               <button onClick={() => setResetModal(null)} className="flex-1 px-4 py-3 border rounded-xl font-semibold">Cancelar</button>
               <button onClick={handleConfirmReset} disabled={resetting} className="flex-1 px-4 py-3 bg-red-500 hover:bg-red-600 text-white rounded-xl font-bold">{resetting ? 'Resetando...' : 'Resetar'}</button>
             </div>
           </div>
        </div>, document.body
      )}

      {/* Confirm Modal */}
      {confirmModal && createPortal(
        <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4" style={{ animation: 'modalBackdropIn 0.25s ease-out' }}>
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setConfirmModal(null)} />
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden relative z-10 border border-gray-100 dark:border-gray-700" style={{ animation: 'modalSlideIn 0.3s cubic-bezier(0.16,1,0.3,1)' }} onClick={e => e.stopPropagation()}>
            <div className="p-6 text-center">
              <div className="w-14 h-14 rounded-2xl bg-[rgba(95,108,55,0.9)] flex items-center justify-center mx-auto mb-4 shadow-lg">
                <DollarSign size={28} className="text-white" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1">Confirmar Pagamento?</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                Parcela {confirmModal.parcela} — <strong className="text-gray-900 dark:text-white">{confirmModal.comissao.parceiro?.full_name}</strong>
              </p>
              <p className="text-3xl font-black text-[#5F6C37] dark:text-[#84cc16] mb-4 tabular-nums">
                {formatCurrency(confirmModal.parcela === 1 ? confirmModal.comissao.parcela_1_valor : confirmModal.comissao.parcela_2_valor)}
              </p>
              <p className="text-xs text-gray-400 bg-gray-50 dark:bg-gray-900/50 px-4 py-2.5 rounded-xl border border-gray-100 dark:border-gray-700 mb-5">
                Você precisará anexar o comprovante PIX no próximo passo.
              </p>
              <div className="flex gap-3">
                <button onClick={() => setConfirmModal(null)} className="flex-1 px-4 py-3 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 rounded-xl text-sm font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">Cancelar</button>
                <button onClick={handleConfirmProceed} className="flex-1 px-4 py-3 bg-[rgba(95,108,55,0.9)] text-white rounded-xl text-sm font-bold flex items-center justify-center gap-2 transition-all">
                  <CheckCircle2 size={16} /> Sim, Pagar
                </button>
              </div>
            </div>
          </div>
        </div>, document.body,
      )}

      {/* Payment Modal */}
      {paymentModal && createPortal(
        <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4" style={{ animation: 'modalBackdropIn 0.25s ease-out' }}>
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={closePaymentModal} />
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden relative z-10 border border-gray-100 dark:border-gray-700 flex flex-col max-h-[90vh]" style={{ animation: 'modalSlideIn 0.3s cubic-bezier(0.16,1,0.3,1)' }} onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center p-5 border-b border-gray-100 dark:border-gray-700/50">
              <h3 className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-[rgba(95,108,55,0.9)] flex items-center justify-center">
                  <DollarSign size={16} className="text-white" />
                </div>
                Registrar Pagamento — P{paymentModal.parcela}
              </h3>
              <button onClick={closePaymentModal} className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
                <X size={18} />
              </button>
            </div>
            <div className="p-5 flex-1 overflow-y-auto">
              <div className="bg-[#212913] p-5 rounded-xl mb-5 text-center">
                <p className="text-[10px] font-bold text-white/50 uppercase tracking-widest mb-1">Valor a Transferir</p>
                <p className="text-3xl font-black text-white tabular-nums">
                  {formatCurrency(paymentModal.parcela === 1 ? paymentModal.comissao.parcela_1_valor : paymentModal.comissao.parcela_2_valor)}
                </p>
                <div className="mt-2 inline-flex items-center px-3 py-1 bg-white/10 rounded-full text-xs text-white/70 font-medium">
                  {paymentModal.comissao.parceiro?.full_name || 'Parceiro'}
                </div>
              </div>
              <div className="mb-5">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Comprovante PIX <span className="text-red-500">*</span></label>
                {!comprovanteFile ? (
                  <label className="flex flex-col items-center gap-3 p-6 border-2 border-dashed border-gray-200 dark:border-gray-600 rounded-xl cursor-pointer hover:border-[#5F6C37] dark:hover:border-[#5F6C37] hover:bg-[#5F6C37]/5 transition-all group">
                    <div className="w-11 h-11 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center group-hover:bg-[#5F6C37]/10 transition-colors">
                      <Upload size={20} className="text-gray-400 group-hover:text-[#5F6C37] transition-colors" />
                    </div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-300">Arraste ou clique para anexar</p>
                    <p className="text-xs text-gray-400 dark:text-gray-500">PNG, JPG ou PDF — máx 5MB</p>
                    <input type="file" className="hidden" accept="image/*,.pdf" onChange={handleFileSelect} />
                  </label>
                ) : (
                  <div className="border border-gray-200 dark:border-gray-600 rounded-xl p-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {comprovantePreview
                        ? <img src={comprovantePreview} alt="" className="w-11 h-11 rounded-lg object-cover border border-gray-200 dark:border-gray-600" />
                        : <div className="w-11 h-11 rounded-lg bg-[#F6A937]/10 flex items-center justify-center"><FileText size={18} className="text-[#F6A937]" /></div>
                      }
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate max-w-[200px]">{comprovanteFile.name}</p>
                        <p className="text-xs text-gray-400">{(comprovanteFile.size / 1024).toFixed(0)} KB</p>
                      </div>
                    </div>
                    <button onClick={() => { setComprovanteFile(null); setComprovantePreview(null); }} className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors">
                      <X size={15} />
                    </button>
                  </div>
                )}
              </div>
              <div className="flex gap-3">
                <button onClick={closePaymentModal} className="flex-1 px-4 py-3 border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 rounded-xl text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">Cancelar</button>
                <button onClick={handleConfirmPayment} disabled={uploading || !comprovanteFile}
                  className="flex-1 px-4 py-3 bg-[rgba(95,108,55,0.9)] text-white rounded-xl text-sm font-bold transition-all disabled:opacity-50 flex items-center justify-center gap-2">
                  {uploading ? <><RefreshCw size={14} className="animate-spin" /> Enviando...</> : <><CheckCircle2 size={14} /> Confirmar</>}
                </button>
              </div>
            </div>
          </div>
        </div>, document.body,
      )}

      <style>{`
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes modalBackdropIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes modalSlideIn {
          from { opacity: 0; transform: scale(0.92) translateY(10px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default ParceirosTab;
